import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FocusLayout from '../components/layouts/FocusLayout';
import '../styles/Login.css';
import LeftPanel from '../components/full_mock_test/attempt/LeftPanel';
import BottomMenu from '../components/full_mock_test/attempt/BottomMenu';
import RightPanel from '../components/full_mock_test/attempt/RightPanel';
import SectionTransition from '../components/full_mock_test/attempt/SectionTransition';
import FullTestResultSummary from '../components/full_mock_test/attempt/FullTestResultSummary';

const FullTestAttempt = ({ isReviewMode = false }) => {
  const { examId, attemptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const isInitialized = useRef(false);

  // Determine type based on URL path
  const isPersonalized = location.pathname.includes('personalized-tests');

  // State Management
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState(null);
  const [examTitle, setExamTitle] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');

  const [examStatus, setExamStatus] = useState('loading'); // 'in_progress', 'section_summary', 'exam_finished'
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [isFitMode, setIsFitMode] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [fillHintSignal, setFillHintSignal] = useState(0);
  const [isGrading, setIsGrading] = useState(false);
  const [sharedColor, setSharedColor] = useState(null);
  // --- Dynamic Zoom Calculation ---
  useEffect(() => {
    const calculateZoom = () => {
      if (isFitMode && contentRef.current) {
        // We need to wait a bit for the DOM to settle after header hide
        setTimeout(() => {
          if (!contentRef.current) return;
          const container = contentRef.current;
          // Temporarily reset zoom to 1 to get real scrollHeight
          const originalZoom = container.style.zoom;
          container.style.zoom = "1";

          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;

          if (scrollHeight > clientHeight) {
            // Add a small buffer (0.95) to ensure it fits comfortably
            const calculatedZoom = (clientHeight / scrollHeight) * 0.98;
            setZoomLevel(Math.max(0.5, calculatedZoom)); // Don't go below 50%
          } else {
            setZoomLevel(1);
          }
          // Restore zoom
          container.style.zoom = originalZoom;
        }, 100);
      } else {
        setZoomLevel(1);
      }
    };

    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => window.removeEventListener('resize', calculateZoom);
  }, [isFitMode, currentExerciseData]);

  // --- Data Fetching ---
  const fetchExercise = useCallback(async (exerciseId) => {
    setExerciseLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const mode = isReviewMode ? 'review' : 'test';
      const attemptQuery = isReviewMode && attemptId ? `&attempt_id=${attemptId}` : '';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/exercises/${exerciseId}?mode=${mode}${attemptQuery}`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch exercise');
      const data = await response.json();
      setCurrentExerciseData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setExerciseLoading(false);
    }
  }, [isReviewMode, attemptId]);

  const fetchExamDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}`, {
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch exam details');
      const data = await response.json();
      setExamTitle(data.title);
    } catch (err) {
      setError(err.message);
    }
  }, [examId]);

  const handleAttemptData = useCallback((data) => {
    // If it's personalized test, normalize data to have an exam_structure
    if (isPersonalized && data.test_detail && !data.exam_structure) {
      data.title = data.test_detail.title;
      data.exam_structure = [{
        section_id: 'personalized',
        section_title: 'Personalized Test',
        total_duration_minutes: 0, // No timer
        exercises: data.test_detail.items.sort((a, b) => a.order_index - b.order_index).map(item => ({
          exercise_id: item.exercise.id,
          ...item.exercise
        }))
      }];
    }

    if (data.exam_structure) {
      data.exam_structure.sort((a, b) => a.order_index - b.order_index);
    }
    setAttempt(data);
    if (data.title) setExamTitle(data.title);
    const firstExerciseId = data.exam_structure && data.exam_structure[0]?.exercises[0]?.exercise_id;
    if (firstExerciseId) {
      fetchExercise(firstExerciseId);
    }
    setExamStatus('in_progress');
  }, [fetchExercise, isPersonalized]);

  const getExistingAttempt = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found. Please log in.');

      let url = `${process.env.REACT_APP_API_URL}/exam-attempts/${id}`;
      if (isPersonalized) {
        url = `${process.env.REACT_APP_API_URL}/client/personalized-tests/attempts/${id}/result`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch existing exam attempt');
      }
      const data = await response.json();
      handleAttemptData(data);
    } catch (err) {
      setError(err.message);
    }
  }, [handleAttemptData, isPersonalized]);

  const createAttempt = useCallback(async (forceNew) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found. Please log in.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exams/${examId}/attempts`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force_new: forceNew })
      });
      if (response.status === 409) {
        const errorData = await response.json();
        getExistingAttempt(errorData.detail.attempt_id);
        return;
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create exam attempt');
      }
      const data = await response.json();
      handleAttemptData(data);
    } catch (err) {
      setError(err.message);
    }
  }, [examId, handleAttemptData]);

  // --- Effects ---
  useEffect(() => {
    if (isInitialized.current) return;

    if (examId) fetchExamDetails();
    if (attemptId) {
      isInitialized.current = true;
      getExistingAttempt(attemptId);
    } else if (examId) {
      isInitialized.current = true;
      createAttempt(false);
    }
  }, [attemptId, examId, createAttempt, getExistingAttempt, fetchExamDetails]);

  // Timer and Section Title effect
  useEffect(() => {
    if (attempt) {
      const currentSection = attempt.exam_structure[currentSectionIndex];
      if (currentSection) {
        if (!isReviewMode) {
          setTimeLeft(currentSection.total_duration_minutes * 60);
        } else {
          setTimeLeft(null);
        }
        setSectionTitle(currentSection.section_title);
      } else {
        setTimeLeft(null);
        setSectionTitle('');
      }
    }
  }, [attempt, currentSectionIndex, isReviewMode]);

  // Countdown effect
  useEffect(() => {
    if (isReviewMode || timeLeft === null || timeLeft <= 0 || examStatus !== 'in_progress') {
      return;
    }
    const timerId = setInterval(() => setTimeLeft(time => time - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isReviewMode, examStatus]);

  const handleNextExercise = () => {
    setShowHint(false);
    setFillHintSignal(0); // Reset so new exercise doesn't auto-fill
    setSharedColor(null); // Reset shared color for Part 5
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    if (currentExerciseIndex < currentSection.exercises.length - 1) {
      const nextExerciseIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextExerciseIndex);
      fetchExercise(currentSection.exercises[nextExerciseIndex].exercise_id);
    } else {
      setExamStatus('section_summary'); // End of section
    }
  };

  const handlePrevExercise = () => {
    setShowHint(false);
    setFillHintSignal(0); // Reset so new exercise doesn't auto-fill
    setSharedColor(null); // Reset shared color for Part 5
    if (!attempt) return;
    if (isReviewMode) {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      } else if (currentSectionIndex > 0) {
        const prevSectionIndex = currentSectionIndex - 1;
        const prevSection = attempt.exam_structure[prevSectionIndex];
        const lastExerciseInPrevSectionIndex = prevSection.exercises.length - 1;
        const prevExerciseId = prevSection.exercises[lastExerciseInPrevSectionIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentSectionIndex(prevSectionIndex);
        setCurrentExerciseIndex(lastExerciseInPrevSectionIndex);
      }
    } else {
      if (currentExerciseIndex > 0) {
        const prevExerciseIndex = currentExerciseIndex - 1;
        const prevExerciseId = attempt.exam_structure[currentSectionIndex].exercises[prevExerciseIndex].exercise_id;
        fetchExercise(prevExerciseId);
        setCurrentExerciseIndex(prevExerciseIndex);
      }
    }
  };

  const handleFillHint = () => {
    setFillHintSignal(prev => prev + 1);
  };

  const handleReviewSection = () => {
    setExamStatus('in_progress');
  };

  const saveSectionAnswers = async () => {
    if (!attempt) return;
    const currentSection = attempt.exam_structure[currentSectionIndex];
    const attemptKey = `attempt_${attempt.attempt_id}`;
    let allSavedAnswers = {};

    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allSavedAnswers = JSON.parse(savedAttempt);
    } catch (e) { console.error("Failed to parse localStorage data:", e); }

    const answersToSubmit = [];
    currentSection.exercises.forEach(exercise => {
      const exerciseAnswers = allSavedAnswers[exercise.exercise_id];
      if (exerciseAnswers) {
        Object.keys(exerciseAnswers).forEach(questionId => {
          answersToSubmit.push({
            question_id: questionId,
            user_answer: [JSON.stringify(exerciseAnswers[questionId])]
          });
        });
      }
    });

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attempt.attempt_id}/answers`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: answersToSubmit, exam_section_id: currentSection.section_id })
      });
      // Clear section answers from local storage after submission
      currentSection.exercises.forEach(exercise => { delete allSavedAnswers[exercise.exercise_id]; });
      localStorage.setItem(attemptKey, JSON.stringify(allSavedAnswers));
    } catch (err) {
      setError(err.message);
    }
  };

  const submitExamAttempt = async () => {
    setIsGrading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication token not found.');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attempt.attempt_id}/submit`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: ''
      });
      if (!response.ok) {
        throw new Error('Failed to submit exam attempt');
      }
      const data = await response.json();

      // Clean up the local storage for this specific attempt to free up space
      localStorage.removeItem(`attempt_${attempt.attempt_id}`);

      // Navigate to the result page, passing the result data in state to avoid a refetch
      navigate(`/full-mock-test/result/${attempt.attempt_id}`, { state: { result: data } });

    } catch (err) {
      setError(err.message);
      setIsGrading(false);
    }
  };

  const proceedToNextSection = () => {
    if (!attempt) return;
    setSharedColor(null); // Reset shared color
    if (currentSectionIndex < attempt.exam_structure.length - 1) {
      const nextSectionIndex = currentSectionIndex + 1;
      setCurrentSectionIndex(nextSectionIndex);
      setCurrentExerciseIndex(0);
      fetchExercise(attempt.exam_structure[nextSectionIndex].exercises[0].exercise_id);
      setExamStatus('in_progress');
    } else {
      if (isReviewMode) {
        if (isPersonalized) {
          navigate('/personalized-tests');
        } else {
          navigate(`/full-mock-test/result/${attempt.attempt_id}`);
        }
      } else {
        setExamStatus('exam_finished');
      }
    }
  };

  const handleNextSection = async () => {
    if (!isReviewMode) {
      await saveSectionAnswers();
      if (currentSectionIndex === attempt.exam_structure.length - 1) {
        await submitExamAttempt();
      }
    }
    proceedToNextSection();
  };

  // --- Render Logic ---
  if (error) return <div className="error-message">Error: {error}</div>;

  return (
    <>
      {isGrading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm animate-fade-in">
          <div className="text-center max-w-md px-6">
            <div className="relative mb-8 inline-block">
              <div className="w-24 h-24 rounded-3xl bg-primary-blue/10 dark:bg-blue-500/20 flex items-center justify-center animate-pulse">
                <span className="material-symbols-outlined text-5xl text-primary-blue dark:text-blue-400">
                  assignment_turned_in
                </span>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-900 animate-bounce">
                <span className="material-symbols-outlined text-white text-xs font-bold">
                  edit
                </span>
              </div>
            </div>
            <h2 className="text-3xl font-display font-black text-slate-800 dark:text-white mb-4">
              Giáo viên đang chấm bài...
            </h2>
            <p className="text-slate-500 dark:text-slate-400 font-body leading-relaxed mb-8">
              Vui lòng chờ trong giây lát. Hệ thống AI đang phân tích bài làm của bạn để đưa ra kết quả và nhận xét chính xác nhất.
            </p>
            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 mb-2 overflow-hidden relative">
              <div className="absolute top-0 bottom-0 bg-primary-blue rounded-full animate-loading-bar" />
            </div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 animate-pulse">
              Processing results with AI Examiner
            </p>
          </div>
        </div>
      )}

      {/* Conditional rendering based on attempt and examStatus */}
      {!attempt ? (
        <div className="exam-attempt-container"><p>Loading...</p></div>
      ) : examStatus === 'in_progress' && currentExerciseData ? (
        <FocusLayout>
          {/* ===== FOCUS SPLIT TOP BAR ===== */}
          <header className="fixed top-0 left-0 right-0 z-50 h-12 bg-white border-b border-gray-200 flex items-center px-6 shadow-sm">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-red-500 text-sm font-headline transition-colors mr-6">← Thoát</button>
            <div className="flex items-center gap-2 mr-6">
              <span className="font-headline font-bold text-hp-on-surface">Part {currentSectionIndex + 1}/{attempt.exam_structure.length}</span>
              <span className="text-gray-300">•</span>
              <span className="bg-hupath-scout/10 text-hupath-scout px-3 py-0.5 rounded-full text-xs font-bold font-headline uppercase">{sectionTitle}</span>
            </div>
            <div className="flex-1 flex items-center justify-center gap-3">
              <span className="font-headline font-semibold text-hp-on-surface text-sm">
                Câu {currentExerciseIndex + 1} / {attempt.exam_structure[currentSectionIndex]?.exercises?.length || 0}
              </span>
              <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="bg-hupath-scout h-full rounded-full transition-all duration-300"
                  style={{ width: `${((currentExerciseIndex + 1) / (attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1)) * 100}%` }} />
              </div>
            </div>
            {!isReviewMode && (
              <div className="flex items-center gap-2 mr-3">
                <button
                  onClick={() => setShowHint(!showHint)}
                  title={showHint ? 'Ẩn gợi ý' : 'Hiện gợi ý'}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${showHint
                    ? 'bg-amber-400 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-400 hover:bg-amber-50 hover:text-amber-500'
                  }`}
                >
                  <span className="material-symbols-outlined text-[18px]">lightbulb</span>
                </button>
                <button
                  onClick={handleFillHint}
                  title="Điền đáp án đúng"
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-all bg-gray-100 text-gray-400 hover:bg-purple-50 hover:text-purple-500"
                >
                  <span className="material-symbols-outlined text-[18px]">auto_fix_high</span>
                </button>
              </div>
            )}
            {timeLeft !== null && (
              <div className="bg-red-50 text-red-600 px-4 py-1.5 rounded-xl font-headline font-bold text-sm border border-red-100 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base">timer</span>
                {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
              </div>
            )}
          </header>

          {/* ===== FOCUS SPLIT MAIN ===== */}
          <div className="flex h-screen pt-12 pb-14 overflow-hidden">

            {/* ===== LEFT PANEL (45%) — Sidebar content ===== */}
            <div className="hidden md:flex flex-col w-[45%] bg-[#F8F6F0] h-full border-r border-gray-200 z-10">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6" style={{ position: 'relative', zIndex: 1 }}>
                <LeftPanel
                    examTitle={examTitle}
                    sectionTitle={sectionTitle}
                    timeLeft={timeLeft}
                    audioUrl={currentExerciseData?.content_data?.audio_url}
                    isStrictAudio={!isReviewMode}
                    partLabel={currentExerciseData?.content_data?.part_label}
                    questionCountLabel={currentExerciseData?.content_data?.question_count_label}
                    instruction={currentExerciseData?.content_data?.instruction}
                    storyText={currentExerciseData?.content_data?.story_text}
                    imageUrl={
                      ['reading_writing_flyer_part_5'].includes(currentExerciseData?.type)
                        ? currentExerciseData?.content_data?.image_url
                        : null
                    }
                    sharedOptions={currentExerciseData?.type === 'listen_flyer_part_3' ? currentExerciseData?.content_data?.shared_options : null}
                    colorOptions={currentExerciseData?.type === 'listen_flyer_part_5' ? currentExerciseData?.questions?.filter(q => !q.question_text?.toLowerCase().startsWith('input')).map(q => q.question_text) : null}
                    inputQuestions={currentExerciseData?.type === 'listen_flyer_part_5' ? currentExerciseData?.questions?.filter(q => q.question_text?.toLowerCase().startsWith('input')) : null}
                    optionBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_4' ? currentExerciseData?.content_data?.option_bank
                        : null
                    }
                    optionBankExampleAnswers={
                      currentExerciseData?.type === 'reading_writing_flyer_part_4' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : null
                    }
                    wordBank={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.content_data?.word_bank
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.content_data?.word_bank
                          : null
                    }
                    wordBankExampleAnswers={
                      currentExerciseData?.type === 'reading_writing_flyer_part_1' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                        : currentExerciseData?.type === 'reading_writing_flyer_part_3' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers
                          : null
                    }
                    answerBank={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.content_data?.answer_bank : null}
                    answerBankExampleAnswers={currentExerciseData?.type === 'reading_writing_flyer_part_2' ? currentExerciseData?.questions?.find(q => q.order_index === 0)?.accepted_answers : null}
                    sharedColor={sharedColor}
                    setSharedColor={setSharedColor}
                  />
                </div>
              </div>

            {/* ===== RIGHT PANEL (55%) — Exercise content ===== */}
            <div className="flex flex-col w-[55%] h-full bg-[#F8F6F0] relative">
              {/* Mobile TopBar */}
              <div className="md:hidden border-b border-gray-100 bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="bg-hupath-scout/10 text-hupath-scout px-3 py-1 rounded-full text-xs font-bold uppercase">{sectionTitle}</span>
                  {timeLeft !== null && (
                    <span className="text-red-600 font-bold text-sm">
                      {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </span>
                  )}
                </div>
                {currentExerciseData?.content_data?.instruction && (
                  <p className="mt-2 text-sm text-hp-on-surface font-medium">{currentExerciseData.content_data.instruction}</p>
                )}
              </div>

              <div
                ref={contentRef}
                className="flex-1 overflow-hidden flex flex-col items-center justify-center p-6"
                style={isFitMode ? { zoom: zoomLevel, position: 'relative', zIndex: 1 } : { position: 'relative', zIndex: 1 }}
              >
                {exerciseLoading ? (
                  <div className="flex items-center justify-center h-full w-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hupath-scout"></div>
                  </div>
                ) : (
                  <div className={`w-full flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 ${currentExerciseData?.type?.includes('listen') ? 'overflow-hidden flex flex-col min-h-0' : 'overflow-y-auto custom-scrollbar'}`}>
                    <RightPanel
                      exercise={currentExerciseData}
                      attemptId={attempt.attempt_id}
                      isReviewMode={isReviewMode}
                      showHint={showHint}
                      fillHintSignal={fillHintSignal}
                      isFitMode={isFitMode}
                      zoomLevel={zoomLevel}
                      sharedColor={sharedColor}
                      setSharedColor={setSharedColor}
                    />
                  </div>
                )}
              </div>

              {/* Mobile BottomMenu */}
              <div className="md:hidden shrink-0 z-10 border-t border-gray-100 bg-white h-14 flex items-center">
                <BottomMenu
                  onPrev={handlePrevExercise}
                  onNext={handleNextExercise}
                  onToggleHint={() => setShowHint(!showHint)}
                  onFillHint={handleFillHint}
                  showHint={showHint}
                  isReviewMode={isReviewMode}
                  currentIndex={currentExerciseIndex}
                  totalCount={attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1}
                />
              </div>
            </div>
          </div>

          {/* ===== FOCUS SPLIT BOTTOM BAR ===== */}
          <footer className="fixed bottom-0 left-0 right-0 z-50 h-14 bg-white border-t border-gray-200 hidden md:flex items-center">
            <BottomMenu
              onPrev={handlePrevExercise}
              onNext={handleNextExercise}
              onToggleHint={() => setShowHint(!showHint)}
              onFillHint={handleFillHint}
              showHint={showHint}
              isReviewMode={isReviewMode}
              hideHint={true}
              currentIndex={currentExerciseIndex}
              totalCount={attempt.exam_structure[currentSectionIndex]?.exercises?.length || 1}
            />
          </footer>
        </FocusLayout>
      ) : examStatus === 'section_summary' ? (
        <div className="exam-attempt-container h-full">
          <SectionTransition
            sectionTitle={attempt.exam_structure[currentSectionIndex].section_title}
            onReview={handleReviewSection}
            onNext={handleNextSection}
            isLastSection={currentSectionIndex === attempt.exam_structure.length - 1}
          />
        </div>
      ) : null /* Exam finished now navigates away, so no need for a final state here */}
    </>
  );
};

export default FullTestAttempt;

